import {alpha, createTheme, darken, lighten} from "@mui/material";
import {
    buildPanelGradient, mix, panelGradientStops
} from "@containers/accountStyle/helpers/buildAccountStyleTokens";
import {CONTRAST_TEXT, CONTRAST_UI, ensureContrast, fitBackground, minContrast} from "@helpers/color/contrast";
import {fromHsl, toHsl} from "@helpers/color/hsl";
import {SxType} from "../../../baseTypes/sx/sxTypes";
import type {IScenePalette} from "./TrackScene";

// Built around VIOLET, the default account menu colour, so the sign in already looks like the
// app it opens.
export const SIGN_IN_COLORS = {
    brand: '#77417a',
    brandDark: '#5c3160',
    ink: '#1a1220',
    blastCore: '#f6b26b',
    blastRing: '#f0714f',
    track: '#d9a3db',
    trackPoint: '#f5e6f5',
    card: '#2a2030',
    cardLine: 'rgba(255, 255, 255, 0.08)',
    muted: '#b9a6bd',
    glowPlum: 'rgba(140, 70, 150, 0.6)',
    glowRaspberry: 'rgba(196, 70, 140, 0.5)',
}

// The map behind the card: at night or by day, switched on the page.
export type SceneLight = 'night' | 'day'

// The page under the scene, tinted with the accent.
const sceneInk = (accent: string, light: SceneLight) =>
    light === 'night' ? mix(accent, '#0e0c11', 0.88) : mix(accent, '#f4f1f6', 0.9)

// The three bus routes: the accent's own hue, then a blue and an amber turn of it, the way violet,
// blue and amber sit by default. A grey accent has no hue to turn, so it keeps the violet.
const ROUTE_HUE_TURNS = [0, -96, 89]
const ROUTE_SATURATIONS = [0.42, 0.55, 0.62]
const DEFAULT_ROUTE_HUE = 306
const GREY_SATURATION = 0.12
// Routes are drawn lines, not text: they need the 3:1 of a graphic against the map.
const ROUTE_LIGHTNESS: Record<SceneLight, number> = {night: 0.75, day: 0.44}

const buildRoutes = (accent: string, light: SceneLight, ink: string) => {
    const {hue, saturation} = toHsl(accent)
    const base = saturation < GREY_SATURATION ? DEFAULT_ROUTE_HUE : hue
    return ROUTE_HUE_TURNS.map((turn, index) => ensureContrast(
        fromHsl({hue: base + turn, saturation: ROUTE_SATURATIONS[index], lightness: ROUTE_LIGHTNESS[light]}),
        [ink], CONTRAST_UI))
}

// The map follows the accent too: the routes take its hue, the streets, the markers and the
// drones a tint of it, and everything that has to be seen is checked against the page under it.
export const buildScenePalette = (accent: string, light: SceneLight): IScenePalette => {
    const ink = sceneInk(accent, light)
    const tracks = buildRoutes(accent, light, ink)

    if (light === 'night') {
        const point = mix(accent, '#ffffff', 0.9)
        const stop = mix(accent, '#141117', 0.88)
        return {
            river: mix(accent, '#1c2130', 0.75),
            street: ensureContrast(mix(accent, '#46424b', 0.7), [ink], 1.8),
            stopFill: stop,
            trackPoint: point,
            markerFill: stop,
            markerStroke: point,
            tracks,
            overlaps: tracks.map((color) => lighten(color, 0.85)),
            drone: mix(accent, '#b4b0b8', 0.85),
            droneWing: mix(accent, '#8a868e', 0.85),
            contrail: point,
        }
    }

    return {
        river: mix(accent, '#c9dcec', 0.85),
        street: ensureContrast(mix(accent, '#bdbac0', 0.75), [ink], 1.8),
        stopFill: '#ffffff',
        trackPoint: ensureContrast(mix(accent, '#4f4a53', 0.8), [ink], CONTRAST_UI),
        markerFill: '#ffffff',
        markerStroke: ensureContrast(mix(accent, '#241e28', 0.85), [ink], CONTRAST_TEXT),
        tracks,
        overlaps: tracks.map((color) => darken(color, 0.55)),
        drone: ensureContrast(mix(accent, '#4b4650', 0.8), [ink], CONTRAST_UI),
        droneWing: mix(accent, '#6d6871', 0.8),
        contrail: mix(accent, '#6d6871', 0.8),
    }
}

// The colours that follow the accent and the light picked on the page. They live in CSS
// variables on :root so the dialog, rendered outside the page, gets them too; the fallbacks are
// the violet night.
const v = (name: string, fallback: string) => `var(--sign-in-${name}, ${fallback})`
const INK = v('ink', SIGN_IN_COLORS.ink)
const CARD = v('card', SIGN_IN_COLORS.card)
const GLOW_MAIN = v('glow-main', SIGN_IN_COLORS.glowPlum)
const GLOW_SECOND = v('glow-second', SIGN_IN_COLORS.glowRaspberry)
const GLOW_AMBER = v('glow-amber', 'rgba(230, 140, 70, 0.38)')
const GLOW_INDIGO = v('glow-indigo', 'rgba(90, 76, 210, 0.42)')
const CAPTION = v('caption', SIGN_IN_COLORS.trackPoint)
const CAPTION_MUTED = v('caption-muted', SIGN_IN_COLORS.muted)
const READOUT = v('readout', SIGN_IN_COLORS.track)
// The top bar and the sign in card: dark at night, soft and tinted with the accent by day,
// where the fields turn grey instead.
const TOP_BAR_BG = v('top-bar-bg', 'rgba(16, 10, 20, 0.86)')
const TOP_BAR_TEXT = v('top-bar-text', SIGN_IN_COLORS.trackPoint)
const TOP_BAR_MUTED = v('top-bar-muted', SIGN_IN_COLORS.muted)
const TOP_BAR_LINE = v('top-bar-line', SIGN_IN_COLORS.cardLine)
const FORM_BG = v('form-bg', SIGN_IN_COLORS.card)
const FORM_TEXT = v('form-text', SIGN_IN_COLORS.trackPoint)
const FORM_MUTED = v('form-muted', SIGN_IN_COLORS.muted)
const FORM_LINE = v('form-line', SIGN_IN_COLORS.cardLine)
const FORM_LINK = v('form-link', SIGN_IN_COLORS.track)
const FIELD_BG = v('field-bg', 'rgba(255, 255, 255, 0.04)')
const FIELD_LINE = v('field-line', 'rgba(255, 255, 255, 0.18)')
const FIELD_HOVER = v('field-hover', 'rgba(255, 255, 255, 0.4)')
const FIELD_TEXT = v('field-text', SIGN_IN_COLORS.trackPoint)
const FIELD_LABEL = v('field-label', SIGN_IN_COLORS.muted)
const AUTOFILL_BG = v('autofill-bg', SIGN_IN_COLORS.card)
const SUBMIT_BG = v('submit-bg', SIGN_IN_COLORS.brand)
const SUBMIT_HOVER = v('submit-hover', SIGN_IN_COLORS.brandDark)
const SUBMIT_TEXT = v('submit-text', '#ffffff')
const DEMO_LINE = v('demo-line', 'rgba(255, 255, 255, 0.35)')
const DEMO_HOVER_LINE = v('demo-hover-line', SIGN_IN_COLORS.trackPoint)
const DEMO_HOVER_BG = v('demo-hover-bg', 'rgba(255, 255, 255, 0.06)')

const inkAlpha = (percent: number) => `color-mix(in srgb, ${INK} ${percent}%, transparent)`

const WHITE = '#ffffff'
const DAY_FIELD_BG = '#f1eff3'
const DAY_FIELD_TEXT = '#231a26'
const DAY_FIELD_LABEL = '#6a6270'

// The top bar by day: the same soft gradient as the app's side menu.
const topBarStops = (color: string) => [darken(color, 0.12), lighten(color, 0.06)]

// The accent the buttons, the top bar and the day card get painted with: the picked one, darkened
// only as far as it takes for white text to read on every shade of them. A pale custom colour
// keeps its hue instead of turning the page white on white.
export const fitSignInAccent = (accent: string) => fitBackground(accent, WHITE, CONTRAST_TEXT,
    (color) => [...panelGradientStops(color), ...topBarStops(color)])

const nightVars = (accent: string, surface: string): Record<string, string> => ({
    '--sign-in-ink': sceneInk(accent, 'night'),
    '--sign-in-glow-main': alpha(lighten(accent, 0.1), 0.6),
    '--sign-in-glow-second': alpha(lighten(accent, 0.25), 0.45),
    '--sign-in-glow-amber': 'rgba(230, 140, 70, 0.38)',
    '--sign-in-glow-indigo': 'rgba(90, 76, 210, 0.42)',
    '--sign-in-caption': SIGN_IN_COLORS.trackPoint,
    '--sign-in-caption-muted': SIGN_IN_COLORS.muted,
    '--sign-in-readout': SIGN_IN_COLORS.track,
    '--sign-in-top-bar-bg': 'rgba(16, 10, 20, 0.86)',
    '--sign-in-top-bar-text': SIGN_IN_COLORS.trackPoint,
    '--sign-in-top-bar-muted': SIGN_IN_COLORS.muted,
    '--sign-in-top-bar-line': SIGN_IN_COLORS.cardLine,
    '--sign-in-form-bg': mix(accent, '#211b26', 0.85),
    '--sign-in-form-text': SIGN_IN_COLORS.trackPoint,
    '--sign-in-form-muted': SIGN_IN_COLORS.muted,
    '--sign-in-form-line': SIGN_IN_COLORS.cardLine,
    '--sign-in-form-link': SIGN_IN_COLORS.track,
    '--sign-in-field-bg': 'rgba(255, 255, 255, 0.04)',
    '--sign-in-field-line': 'rgba(255, 255, 255, 0.18)',
    '--sign-in-field-hover': 'rgba(255, 255, 255, 0.4)',
    '--sign-in-field-text': SIGN_IN_COLORS.trackPoint,
    '--sign-in-field-label': SIGN_IN_COLORS.muted,
    '--sign-in-autofill-bg': mix(accent, '#211b26', 0.85),
    '--sign-in-demo-line': 'rgba(255, 255, 255, 0.35)',
    '--sign-in-demo-hover-line': SIGN_IN_COLORS.trackPoint,
    '--sign-in-demo-hover-bg': 'rgba(255, 255, 255, 0.06)',
    '--sign-in-submit-bg': surface,
    '--sign-in-submit-hover': darken(surface, 0.22),
    '--sign-in-submit-text': WHITE,
})

// By day every colour on the card and the bar follows from the fitted accent and is checked
// against what it sits on, so no pick of colour can make the page fall apart.
const dayVars = (accent: string, surface: string): Record<string, string> => {
    const ink = sceneInk(accent, 'day')
    const card = panelGradientStops(surface)
    const topBar = topBarStops(surface)
    // The caption, the readout and the wordmark sit on the light page, under the drifting glows.
    const page = [ink, WHITE]
    const submitHover = mix(surface, WHITE, 0.88)

    return {
        '--sign-in-ink': ink,
        '--sign-in-glow-main': alpha(lighten(accent, 0.35), 0.35),
        '--sign-in-glow-second': alpha(lighten(accent, 0.55), 0.35),
        '--sign-in-glow-amber': 'rgba(240, 170, 110, 0.28)',
        '--sign-in-glow-indigo': 'rgba(120, 130, 230, 0.22)',
        '--sign-in-caption': ensureContrast(darken(accent, 0.38), page, CONTRAST_TEXT),
        '--sign-in-caption-muted': ensureContrast(mix(accent, '#4a4450', 0.65), page, CONTRAST_TEXT),
        '--sign-in-readout': ensureContrast(darken(accent, 0.18), page, CONTRAST_TEXT),
        '--sign-in-top-bar-bg': `linear-gradient(90deg, ${topBar.join(', ')})`,
        '--sign-in-top-bar-text': WHITE,
        '--sign-in-top-bar-muted': ensureContrast('rgba(255, 255, 255, 0.82)', topBar, CONTRAST_TEXT),
        '--sign-in-top-bar-line': 'rgba(255, 255, 255, 0.14)',
        '--sign-in-form-bg': buildPanelGradient(surface),
        '--sign-in-form-text': WHITE,
        '--sign-in-form-muted': ensureContrast('rgba(255, 255, 255, 0.8)', card, CONTRAST_TEXT),
        '--sign-in-form-line': 'rgba(255, 255, 255, 0.2)',
        '--sign-in-form-link': WHITE,
        // The fields stay light grey with dark text, the one light surface on the card.
        '--sign-in-field-bg': DAY_FIELD_BG,
        '--sign-in-field-line': 'rgba(255, 255, 255, 0)',
        '--sign-in-field-hover': 'rgba(255, 255, 255, 0.7)',
        '--sign-in-field-text': DAY_FIELD_TEXT,
        '--sign-in-field-label': DAY_FIELD_LABEL,
        '--sign-in-autofill-bg': DAY_FIELD_BG,
        // An outlined button: its edge is all there is of it.
        '--sign-in-demo-line': ensureContrast('rgba(255, 255, 255, 0.6)', card, CONTRAST_UI),
        '--sign-in-demo-hover-line': WHITE,
        '--sign-in-demo-hover-bg': 'rgba(255, 255, 255, 0.12)',
        '--sign-in-submit-bg': WHITE,
        '--sign-in-submit-hover': submitHover,
        '--sign-in-submit-text': ensureContrast(darken(surface, 0.25), [WHITE, submitHover], CONTRAST_TEXT),
    }
}

// What has to read on what by day: a colour, the background it sits on and the minimum.
const DAY_PAIRS: [string, string, number][] = [
    ['--sign-in-top-bar-text', '--sign-in-top-bar-bg', CONTRAST_TEXT],
    ['--sign-in-top-bar-muted', '--sign-in-top-bar-bg', CONTRAST_TEXT],
    ['--sign-in-form-text', '--sign-in-form-bg', CONTRAST_TEXT],
    ['--sign-in-form-muted', '--sign-in-form-bg', CONTRAST_TEXT],
    ['--sign-in-demo-line', '--sign-in-form-bg', CONTRAST_UI],
    ['--sign-in-field-bg', '--sign-in-form-bg', CONTRAST_UI],
    ['--sign-in-field-text', '--sign-in-field-bg', CONTRAST_TEXT],
    ['--sign-in-field-label', '--sign-in-field-bg', CONTRAST_TEXT],
    ['--sign-in-submit-text', '--sign-in-submit-bg', CONTRAST_TEXT],
    ['--sign-in-submit-text', '--sign-in-submit-hover', CONTRAST_TEXT],
    ['--sign-in-caption', '--sign-in-ink', CONTRAST_TEXT],
    ['--sign-in-caption-muted', '--sign-in-ink', CONTRAST_TEXT],
    ['--sign-in-readout', '--sign-in-ink', CONTRAST_TEXT],
]

// The colours a background paints: the stops of a gradient, or the colour itself.
const paintedColors = (value: string) => value.startsWith('linear-gradient')
    ? value.match(/#[0-9a-f]{3,8}\b|rgba?\([^)]*\)/gi) ?? []
    : [value]

// In development, names the pair that stopped reading when a colour above gets changed by hand.
const warnLowContrast = (vars: Record<string, string>) => {
    DAY_PAIRS.forEach(([color, background, min]) => {
        const ratio = minContrast(vars[color], paintedColors(vars[background]))
        if (ratio < min) {
            console.warn(`Sign in: ${color} on ${background} reads at ${ratio.toFixed(2)}:1, under ${min}:1`)
        }
    })
}

export const buildSignInVars = (accent: string, light: SceneLight): Record<string, string> => {
    const surface = fitSignInAccent(accent)
    const vars = {
        // The reward cards stay dark either way: they belong to the scene, not to the form.
        '--sign-in-card': mix(accent, '#211b26', 0.85),
        ...(light === 'night' ? nightVars(accent, surface) : dayVars(accent, surface)),
    }
    if (light === 'day' && process.env.NODE_ENV === 'development') {
        warnLowContrast(vars)
    }
    return vars
}

const typography = {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
}

// The scene and the reward cards keep their own dark theme whatever the user picked in the app.
export const buildSignInTheme = (accent: string) => {
    const surface = fitSignInAccent(accent)
    return createTheme({
        palette: {
            mode: 'dark',
            primary: {main: surface, dark: darken(surface, 0.22), contrastText: WHITE},
            text: {primary: SIGN_IN_COLORS.trackPoint, secondary: SIGN_IN_COLORS.muted},
            background: {default: mix(accent, '#0e0c11', 0.88), paper: mix(accent, '#211b26', 0.85)},
        },
        typography,
    })
}

export const DISPLAY_FONT = '"Unbounded", "Roboto", sans-serif'
export const MONO_FONT = 'ui-monospace, "Cascadia Mono", Consolas, "Roboto Mono", monospace'

const focusRing = {
    outline: `2px solid ${SIGN_IN_COLORS.track}`,
    outlineOffset: '2px',
}

const sceneFocusRing = {
    outline: `2px solid ${READOUT}`,
    outlineOffset: '2px',
}

const glow = (color: string, size: string, top: string, left: string, animation: string) => ({
    position: 'absolute' as const,
    top,
    left,
    width: size,
    height: size,
    borderRadius: '50%',
    background: `radial-gradient(circle, ${color} 0%, transparent 65%)`,
    animation,
    willChange: 'transform',
    '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
    },
})

// Looks like a status light; it is the hidden switch of the air defence scene. The button is
// larger than the dot so it can be hit, and it takes clicks although the caption doesn't.
const liveDot = (color: string) => ({
    width: 20,
    height: 20,
    m: '-6px',
    p: 0,
    border: 'none',
    borderRadius: '50%',
    background: 'transparent',
    cursor: 'pointer',
    pointerEvents: 'auto' as const,
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
    '&::after': {
        content: '""',
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: color,
        animation: 'signInPulse 2s ease-in-out infinite',
    },
    '@keyframes signInPulse': {
        '0%, 100%': {opacity: 1},
        '50%': {opacity: 0.25},
    },
    '@media (prefers-reduced-motion: reduce)': {
        '&::after': {animation: 'none'},
    },
    '&:focus-visible': sceneFocusRing,
})

const pill = {
    height: 44,
    borderRadius: '22px',
    textTransform: 'none' as const,
    fontSize: 15,
    boxShadow: 'none',
    '&.Mui-focusVisible': focusRing,
}

export const sx: SxType = {
    root: {
        position: 'fixed',
        inset: 0,
        overflow: 'auto',
        bgcolor: INK,
        color: CAPTION,
        display: 'flex',
        flexDirection: 'column',
        transition: 'background-color 400ms',
    },
    backdrop: {
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        // Whatever moves in the scene stays inside it as far as layout and paint go.
        contain: 'strict',
    },
    glows: {
        position: 'absolute',
        inset: 0,
        // Colour fields that slowly swap places and swell, the way the IntelliJ splash shimmers.
        '@keyframes signInDriftA': {
            '0%, 100%': {transform: 'translate(0, 0) scale(1)'},
            '33%': {transform: 'translate(14vw, 10vh) scale(1.15)'},
            '66%': {transform: 'translate(-6vw, 18vh) scale(0.9)'},
        },
        '@keyframes signInDriftB': {
            '0%, 100%': {transform: 'translate(0, 0) scale(1)'},
            '33%': {transform: 'translate(-16vw, -8vh) scale(0.85)'},
            '66%': {transform: 'translate(-4vw, -18vh) scale(1.2)'},
        },
        '@keyframes signInDriftC': {
            '0%, 100%': {transform: 'translate(0, 0) scale(1)'},
            '50%': {transform: 'translate(18vw, -14vh) scale(1.25)'},
        },
        '@keyframes signInDriftD': {
            '0%, 100%': {transform: 'translate(0, 0) scale(1.1)'},
            '50%': {transform: 'translate(-14vw, 16vh) scale(0.9)'},
        },
    },
    glowPlum: glow(GLOW_MAIN, '70vmax', '-25vmax', '20vw', 'signInDriftA 26s ease-in-out infinite'),
    glowRaspberry: glow(GLOW_SECOND, '55vmax', '35vh', '55vw', 'signInDriftB 30s ease-in-out infinite'),
    glowAmber: glow(GLOW_AMBER, '45vmax', '55vh', '28vw', 'signInDriftC 22s ease-in-out infinite'),
    glowIndigo: glow(GLOW_INDIGO, '50vmax', '-10vh', '70vw', 'signInDriftD 34s ease-in-out infinite'),
    shade: {
        position: 'absolute',
        inset: 0,
        background: `linear-gradient(90deg, ${inkAlpha(80)} 0%, ${inkAlpha(0)} 45%), `
            + `linear-gradient(180deg, ${inkAlpha(0)} 60%, ${inkAlpha(85)} 100%)`,
    },
    topBar: {
        position: 'relative',
        flexShrink: 0,
        height: 56,
        px: {xs: 2, md: 4},
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: TOP_BAR_BG,
        color: TOP_BAR_TEXT,
        borderBottom: `1px solid ${TOP_BAR_LINE}`,
    },
    brandMark: {
        fontFamily: DISPLAY_FONT,
        fontWeight: 600,
        fontSize: 15,
        letterSpacing: '0.08em',
    },
    score: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.5,
        borderRadius: '14px',
        border: `1px solid ${SIGN_IN_COLORS.cardLine}`,
        bgcolor: 'rgba(240, 113, 79, 0.12)',
        fontFamily: MONO_FONT,
        fontSize: 12,
        lineHeight: '16px',
        color: SIGN_IN_COLORS.blastCore,
        whiteSpace: 'nowrap',
        '& > *': {lineHeight: 'inherit'},
    },
    scoreLabel: {
        fontFamily: MONO_FONT,
        fontSize: 12,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: SIGN_IN_COLORS.muted,
    },
    topBarTools: {
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
    },
    lightToggle: {
        color: TOP_BAR_MUTED,
        '&.Mui-focusVisible': focusRing,
    },
    language: {
        ml: 1,
        fontSize: 14,
        color: TOP_BAR_MUTED,
        '& .MuiSelect-select': {
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
        },
        '& .MuiSelect-icon': {
            color: TOP_BAR_MUTED,
        },
        '& .MuiSelect-select:focus-visible': focusRing,
    },
    body: {
        // Lets clicks through to the drones; only the card takes them.
        pointerEvents: 'none',
        position: 'relative',
        flex: '1 0 auto',
        display: 'flex',
        alignItems: 'center',
        px: {xs: 2, md: '8vw'},
        py: {xs: 3, md: 5},
    },
    card: {
        pointerEvents: 'auto',
        width: '100%',
        maxWidth: {xs: 440, md: 380},
        mx: {xs: 'auto', md: 0},
        p: {xs: 3, sm: 3.5},
        borderRadius: '12px',
        background: FORM_BG,
        color: FORM_TEXT,
        border: `1px solid ${FORM_LINE}`,
        boxShadow: '0 18px 48px rgba(10, 6, 14, 0.18)',
    },
    title: {
        fontSize: {xs: 26, md: 28},
        fontWeight: 700,
        letterSpacing: '-0.01em',
        lineHeight: 1.2,
    },
    subtitle: {
        mt: 1,
        mb: 3,
        fontSize: 14,
        color: FORM_MUTED,
    },
    field: {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            bgcolor: FIELD_BG,
        },
        '& .MuiInputBase-input': {
            color: FIELD_TEXT,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: FIELD_LINE,
        },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: FIELD_HOVER,
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: FORM_LINK,
            borderWidth: 2,
        },
        // Inside the field the label sits on the field; raised, it sits on the card.
        '& .MuiInputLabel-root': {
            color: FIELD_LABEL,
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            color: FORM_MUTED,
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: FORM_LINK,
        },
        // The filled fields of the day card: label and text inside a grey well.
        '& .MuiFilledInput-root': {
            borderRadius: '12px',
            bgcolor: FIELD_BG,
            border: '2px solid transparent',
            transition: 'border-color 150ms',
            '&:hover, &.Mui-focused': {
                bgcolor: FIELD_BG,
            },
            '&.Mui-focused': {
                borderColor: FORM_LINK,
            },
        },
        '& .MuiInputLabel-filled, & .MuiInputLabel-filled.MuiInputLabel-shrink, & .MuiInputLabel-filled.Mui-focused': {
            color: FIELD_LABEL,
        },
        // Keeps the browser's autofill from painting the fields light yellow.
        '& input:-webkit-autofill': {
            WebkitBoxShadow: `0 0 0 100px ${AUTOFILL_BG} inset`,
            WebkitTextFillColor: FIELD_TEXT,
            borderRadius: 'inherit',
        },
    },
    forgot: {
        alignSelf: 'flex-end',
        mt: -1,
        fontSize: 13,
        color: FORM_LINK,
        textDecorationColor: `color-mix(in srgb, ${FORM_LINK} 40%, transparent)`,
        '&:focus-visible': focusRing,
    },
    hint: {
        fontSize: 13,
        color: FORM_MUTED,
        borderLeft: `3px solid ${FORM_LINK}`,
        pl: 1.5,
    },
    submit: {
        ...pill,
        mt: 0.5,
        fontWeight: 600,
        bgcolor: SUBMIT_BG,
        color: SUBMIT_TEXT,
        '&:hover': {
            bgcolor: SUBMIT_HOVER,
            boxShadow: 'none',
        },
        '&.Mui-disabled': {
            bgcolor: SUBMIT_BG,
            color: SUBMIT_TEXT,
            opacity: 0.75,
        },
    },
    divider: {
        my: 0.5,
        fontSize: 13,
        color: FORM_MUTED,
        '&::before, &::after': {
            borderColor: FORM_LINE,
        },
    },
    demo: {
        ...pill,
        color: FORM_TEXT,
        borderColor: DEMO_LINE,
        '&:hover': {
            borderColor: DEMO_HOVER_LINE,
            bgcolor: DEMO_HOVER_BG,
        },
    },
    cardFooter: {
        mt: 3,
        pt: 2,
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 12,
        color: FORM_MUTED,
        borderTop: `1px solid ${FORM_LINE}`,
        '& a': {
            color: 'inherit',
        },
        '& a:focus-visible': focusRing,
    },
    dialogPaper: {
        maxWidth: 440,
        borderRadius: '12px',
        bgcolor: CARD,
        border: `1px solid ${SIGN_IN_COLORS.cardLine}`,
        backgroundImage: 'none',
    },
    dialogTitle: {
        fontWeight: 700,
        fontSize: 20,
    },
    dialogText: {
        fontSize: 14,
        lineHeight: 1.55,
        color: SIGN_IN_COLORS.muted,
    },
    dialogActions: {
        px: 3,
        pb: 2.5,
        gap: 1,
        '& > :not(:first-of-type)': {ml: 0},
        '& .MuiButton-root': {px: 2.5, mt: 0},
    },
    reward: {
        position: 'fixed',
        top: 72,
        right: {xs: 16, md: 48},
        width: {xs: 'calc(100% - 32px)', sm: 300},
        p: 2,
        borderRadius: '12px',
        bgcolor: CARD,
        color: SIGN_IN_COLORS.trackPoint,
        border: `1px solid ${SIGN_IN_COLORS.cardLine}`,
        boxShadow: '0 18px 48px rgba(0, 0, 0, 0.45)',
        animation: 'rewardIn 380ms ease-out',
        '@keyframes rewardIn': {
            from: {opacity: 0, transform: 'translateY(-12px) scale(0.97)'},
            to: {opacity: 1, transform: 'none'},
        },
        '@keyframes rewardFlicker': {
            from: {transform: 'scaleY(0.82)'},
            to: {transform: 'scaleY(1.12)'},
        },
        '@keyframes rewardDrift': {
            from: {transform: 'none'},
            to: {transform: 'translate(var(--dx), var(--dy)) rotate(var(--r))'},
        },
        '@keyframes rewardGoUnder': {
            '0%': {transform: 'translateY(0)', opacity: 1},
            '70%': {transform: 'translateY(112px)', opacity: 1},
            '100%': {transform: 'translateY(112px)', opacity: 0},
        },
        '@keyframes rewardDove': {
            '0%, 60%': {transform: 'translate(-40px, 12px)', opacity: 0},
            '100%': {transform: 'none', opacity: 1},
        },
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            '& *': {animation: 'none !important'},
        },
    },
    rewardClose: {
        position: 'absolute',
        top: 6,
        right: 6,
        color: SIGN_IN_COLORS.muted,
        '&.Mui-focusVisible': sceneFocusRing,
    },
    rewardTitle: {
        mt: 1,
        fontFamily: DISPLAY_FONT,
        fontWeight: 600,
        fontSize: 15,
        color: SIGN_IN_COLORS.blastCore,
    },
    rewardGlory: {
        mt: 1.5,
        pt: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderTop: `1px solid ${SIGN_IN_COLORS.cardLine}`,
        fontWeight: 700,
        fontSize: 14,
        color: '#ffd700',
    },
    rewardText: {
        mt: 0.5,
        fontSize: 13,
        color: SIGN_IN_COLORS.muted,
    },
    caption: {
        display: {xs: 'none', md: 'block'},
        position: 'fixed',
        right: 48,
        bottom: 40,
        textAlign: 'right',
        pointerEvents: 'none',
    },
    wordmark: {
        fontFamily: DISPLAY_FONT,
        fontWeight: 800,
        fontSize: 'clamp(52px, 5.6vw, 92px)',
        lineHeight: 0.9,
        letterSpacing: '-0.02em',
        color: CAPTION,
    },
    taglineTitle: {
        mt: 1.5,
        fontSize: 18,
        fontWeight: 600,
        color: CAPTION,
    },
    taglineText: {
        mt: 0.75,
        ml: 'auto',
        maxWidth: 420,
        fontSize: 14,
        lineHeight: 1.5,
        color: CAPTION_MUTED,
    },
    readout: {
        mt: 2.5,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 1.5,
        fontFamily: MONO_FONT,
        fontSize: 13,
        color: READOUT,
        whiteSpace: 'nowrap',
    },
    readoutLabel: {
        fontFamily: MONO_FONT,
        fontSize: 11,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: CAPTION_MUTED,
    },
    liveDot: liveDot(READOUT),
    liveDotDefence: liveDot(SIGN_IN_COLORS.blastRing),
}
